import React, { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { setScreenFormData } from "../../state/slices/formsSlice";
import { store } from "../../state/store";
import { apiService } from "../../services/api";
import HrmStatCards from "./HrmStatCards";
import { getUserRole } from "../../utils/visibilityUtils";

type CardConfig = {
  name?: string;
  bindValue?: string;
  icon?: React.ReactNode;
  defaultValue?: number;
  valueFormat?: string;
  color?: string;
  description?: string;
};

interface InfoCardsWithStatsProps {
  screenId: string;
  infoCardsConfig: {
    apiname?: string;
    cards?: CardConfig[];
    variant?: "default" | "compact";
  };
  formData: Record<string, any>;
}

function normalizeHrmsInfoCardsPath(apiname: string | undefined): string | null {
  if (!apiname || typeof apiname !== "string") return null;
  const trimmed = apiname.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("/pabws/")) return trimmed;
  if (trimmed.startsWith("/g360/")) return `/pabws${trimmed.slice("/g360".length)}`;
  if (trimmed.startsWith("/hrms/")) return `/pabws${trimmed}`;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function defaultCalendarMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtYyyyMmDd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function currentMonthDateRange(): { start_date: string; end_date: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start_date: fmtYyyyMmDd(start), end_date: fmtYyyyMmDd(now) };
}

/**
 * HRMS stat cards: GET `apiname` (normalized to /pabws/...) and merge bindValue fields into screen formData.
 */
const InfoCardsWithStats: React.FC<InfoCardsWithStatsProps> = ({
  screenId,
  infoCardsConfig,
  formData,
}) => {
  const dispatch = useAppDispatch();
  const branchId = useAppSelector((state) => state.auth?.user?.branch_id);
  const employeeId = useAppSelector((state) => state.auth?.user?.employee_id);
  const apiname = infoCardsConfig.apiname;
  const cards = infoCardsConfig.cards ?? [];
  const cardBindKeys = useMemo(() => {
    const c = infoCardsConfig.cards ?? [];
    return c.map((x) => x.bindValue || x.name || "").join("|");
  }, [infoCardsConfig.cards]);

  useEffect(() => {
    const path = normalizeHrmsInfoCardsPath(apiname);
    if (!path) return;

    let cancelled = false;
    const run = async () => {
      try {
        const query: Record<string, string> = {};
        if (path.includes("payroll/statistics")) {
          const ym = String(formData?.payroll_month_filter || "").trim() || defaultCalendarMonth();
          query.payroll_month = ym;
          if (branchId) {
            query.branch_id = String(branchId);
          }
        }
        if (path.includes("attendance/statistics")) {
          const roleId = getUserRole();
          const { start_date, end_date } = currentMonthDateRange();
          query.start_date = start_date;
          query.end_date = end_date;
          // For employee users, always scope to their own employee_id.
          // Admin users omit employee_id to get global/current-month totals by default.
          if (roleId === 2 && employeeId) {
            query.employee_id = String(employeeId);
          }
        }

        const stats = await apiService.get<Record<string, unknown>>(
          path,
          Object.keys(query).length ? query : undefined,
          {
            includeAuthToken: true,
            authHeaderType: "Bearer",
            errorHandling: "return-null",
          }
        );
        if (cancelled || !stats || typeof stats !== "object") return;

        const payload = stats as Record<string, any>;
        const dataPayload =
          payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
            ? (payload.data as Record<string, any>)
            : payload;

        const keys = (infoCardsConfig.cards ?? [])
          .map((c) => c.bindValue)
          .filter(Boolean) as string[];
        const patch: Record<string, any> = {};
        for (const k of keys) {
          if (dataPayload[k] !== undefined && dataPayload[k] !== null) {
            patch[k] = dataPayload[k];
          }
        }
        if (Object.keys(patch).length === 0) return;

        const current = store.getState().forms.formDataByScreen[screenId] || {};
        dispatch(
          setScreenFormData({
            screenId,
            data: { ...current, ...patch },
          })
        );
      } catch {
        /* ignore */
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [apiname, screenId, dispatch, cardBindKeys, formData?.payroll_month_filter, branchId, employeeId]);

  const stats = cards.map((card: CardConfig) => {
    const key = card.bindValue || card.name || "";
    const value =
      formData[key] !== undefined && formData[key] !== null && formData[key] !== ""
        ? formData[key]
        : card.defaultValue ?? 0;

    return {
      id: key || card.name || "card",
      label: card.name || key,
      value,
      icon: card.icon,
      accentColor: card.color || "#3B82F6",
      description: card.description || "",
    };
  });

  return <HrmStatCards stats={stats} variant={infoCardsConfig.variant || "default"} />;
};

export default InfoCardsWithStats;
